import { type ReactNode, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useOrganization } from "../context/OrganizationContext";
import { NoOrganizationScreen } from "./NoOrganizationScreen";
import { LegalLinks } from "../pages/legal/LegalLinks";

interface ContextGuardProps {
  children: ReactNode;
}

/** How often to auto-retry when the organization fetch has failed */
const AUTO_RETRY_INTERVAL = 10_000; // 10 seconds

/**
 * ContextGuard — gates rendering until auth + organization context are ready.
 *
 * UX rules:
 *  - Before authentication completes → Haven splash; once auth resolves as
 *    unauthenticated, call login() (Auth0 needs it; Keycloak redirects itself).
 *  - While organization data is loading (or retrying) → Haven-branded splash.
 *  - After all retries exhausted → stays on splash, auto-retries every 10 s.
 *  - User has zero organizations → NoOrganizationScreen (branded, actionable).
 *  - Everything ready → render children.
 *
 * End users should never see internal state names or raw error messages.
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5, 9.7, 11.1, 15.4
 */
export function ContextGuard({ children }: ContextGuardProps) {
  const { loading: authLoading, authenticated, login } = useAuth();
  const { isInitialized, isLoading, error, organizations, refreshOrganizations } = useOrganization();
  const location = useLocation();
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Auto-retry silently in the background when org fetch has errored out
  useEffect(() => {
    if (error && authenticated && !location.pathname.startsWith('/admin')) {
      retryTimerRef.current = setTimeout(() => {
        refreshOrganizations();
      }, AUTO_RETRY_INTERVAL);
    }
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [error, authenticated, location.pathname, refreshOrganizations]);

  // Redirect to login when auth is resolved but user is not authenticated.
  // Keycloak handles this automatically via "login-required" onLoad; Auth0
  // requires an explicit loginWithRedirect() call.
  // Guard: skip redirect while Auth0 is processing a callback (code+state in
  // URL) — that window briefly has !authenticated before the SDK exchanges the
  // code, and calling login() again causes a rate-limit loop.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isAuth0Callback = params.has('code') && params.has('state');
    if (!authLoading && !authenticated && !isAuth0Callback && !location.pathname.startsWith('/admin')) {
      login();
    }
  }, [authLoading, authenticated, login, location.pathname]);

  // ── Admin routes bypass org context entirely ──────────────────────────
  if (location.pathname.startsWith('/admin')) {
    return <>{children}</>;
  }

  // ── Auth not ready / not authenticated → show splash while redirecting ──
  if (authLoading || !authenticated) {
    return <HavenSplash />;
  }

  // ── Org loading, retrying, or errored — branded splash ────────────────
  if (!isInitialized || isLoading || error) {
    return <HavenSplash />;
  }

  // ── No organizations — branded creation screen ────────────────────────
  if (organizations.length === 0) {
    return <NoOrganizationScreen />;
  }

  // ── Ready ─────────────────────────────────────────────────────────────
  return <>{children}</>;
}

/**
 * Minimal branded splash screen shown while Haven initializes.
 * Uses inline styles so it renders reliably before any CSS bundles load.
 */
function HavenSplash() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontSize: '2.25rem',
            fontWeight: 700,
            color: '#ffffff',
            marginBottom: '1.5rem',
            letterSpacing: '-0.025em',
          }}
        >
          Haven
        </div>
        <div
          style={{
            width: '1.75rem',
            height: '1.75rem',
            border: '3px solid rgba(255,255,255,0.15)',
            borderTopColor: '#ffffff',
            borderRadius: '50%',
            margin: '0 auto',
            animation: 'haven-guard-spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes haven-guard-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ marginTop: '2rem', color: '#94a3b8' }}>
          <LegalLinks />
        </div>
      </div>
    </div>
  );
}
